import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { usePrefs } from './PrefsContext';
import { getFriendsDashboard, addFriend, removeFriend, updateGamePlay, createEmptyScoreRecord } from '../services/gameService';
import * as gameActivityService from '../services/gameActivityService';

/**
 * Plays, friends, and what's happened today.
 *
 * Everything here is server-derived under D22. In particular:
 *
 *   gamesPlayedToday - the games with a `plays` row for today, score or not.
 *                      Clicking a game link writes one, so "played today" is
 *                      a fact about the account rather than about this
 *                      browser.
 *   streak           - computed from the distinct days the user has plays on,
 *                      so it's honest and survives a new device.
 *
 * The recent-game-click marker is the one thing still read from localStorage,
 * and deliberately so - see gameActivityService.
 */
const PlayContext = createContext();

export const PlayProvider = ({ children }) => {
    const { user, isLoading: authLoading } = useAuth();
    // Only for analytics - PlayProvider sits inside PrefsProvider.
    const { favorites } = usePrefs();

    const [friendsData, setFriendsData] = useState(null);
    const [friendsLoading, setFriendsLoading] = useState(false);
    const [friendsError, setFriendsError] = useState(null);

    const friendsDataRef = useRef(friendsData);
    const inFlightRef = useRef(null);
    const fetchedAtRef = useRef(0);
    useEffect(() => { friendsDataRef.current = friendsData; }, [friendsData]);

    // When the dashboard currently held was fetched, as an epoch ms. Read
    // through a stable function rather than exposed as state, so a caller can
    // check freshness inside an effect without that effect re-running on every
    // load. App.js uses it to decide whether returning from a game needs a
    // fresh read or can use what's already here.
    const getFriendsDataFetchedAt = useCallback(() => fetchedAtRef.current, []);

    // Stable identity (empty deps) so components that depend on this in an
    // effect don't get re-triggered every time the data changes - that was a
    // real infinite-request loop once, see api/CLAUDE.md.
    const loadFriendsData = useCallback(async (forceRefresh = false) => {
        // A caller that doesn't need a fresh read takes what's already known,
        // or joins a request already on its way. Returning from a game used to
        // fire two cold round trips for one answer - the mount load and the
        // auto-score check - and the second one was what the user waited on.
        if (!forceRefresh) {
            if (friendsDataRef.current) return friendsDataRef.current;
            if (inFlightRef.current) return inFlightRef.current;
        }

        setFriendsLoading(true);
        setFriendsError(null);

        // A forced refresh always starts its own request. The mutation handlers
        // use it, and joining a request that started before the write would
        // hand back pre-write data.
        const request = (async () => {
            try {
                const data = await getFriendsDashboard();
                setFriendsData(data);
                friendsDataRef.current = data;
                fetchedAtRef.current = Date.now();
                return data;
            } catch (error) {
                console.log('Error loading friends data:', error);
                setFriendsError('Failed to load your scores. Please try again.');
                throw error;
            } finally {
                if (inFlightRef.current === request) inFlightRef.current = null;
                setFriendsLoading(false);
            }
        })();

        inFlightRef.current = request;
        return request;
    }, []);

    // Load once there's an identity, and reload when it changes (sign in,
    // sign out, or a guest record coming into existence). Keyed on the id
    // alone so an unrelated change to the user object doesn't refetch.
    const userId = user?.id ?? null;
    useEffect(() => {
        if (authLoading) return;

        if (!userId) {
            setFriendsData(null);
            friendsDataRef.current = null;
            fetchedAtRef.current = 0;
            setFriendsError(null);
            return;
        }

        // Drop the previous identity's data before loading, so this doesn't
        // need a forced read to avoid serving it. Being unforced is what lets
        // it join a request the auto-score check already started - effects run
        // children-first, so App's has usually fired by the time this does.
        setFriendsData(null);
        friendsDataRef.current = null;
        fetchedAtRef.current = 0;

        loadFriendsData().catch(() => {
            // Already surfaced via friendsError.
        });
    }, [authLoading, userId, loadFriendsData]);

    const addFriendHandler = async (username) => {
        try {
            await addFriend(username);
            await loadFriendsData(true);
            return true;
        } catch (error) {
            console.log('Error adding friend:', error);
            return false;
        }
    };

    const removeFriendHandler = async (friendId) => {
        let originalFriendsData = null;
        try {
            if (friendsData) {
                originalFriendsData = { ...friendsData };
                setFriendsData(prev => ({
                    ...prev,
                    friends: (prev.friends || []).filter(f => f.id !== friendId)
                }));
            }

            await removeFriend(friendId);
            await loadFriendsData(true);
            return true;
        } catch (error) {
            if (originalFriendsData) {
                setFriendsData(originalFriendsData);
            }
            console.log('Error removing friend:', error);
            return false;
        }
    };

    const updatePlayHandler = async (gameId, score, message) => {
        const previous = friendsData;

        if (friendsData && friendsData.user) {
            setFriendsData(prev => ({
                ...prev,
                user: {
                    ...prev.user,
                    today_plays: {
                        ...prev.user.today_plays,
                        [gameId]: { score, message }
                    }
                }
            }));
        }

        try {
            await updateGamePlay({ gameId, score, message: message || '' });
            // Re-read rather than patching in place: the score just logged may
            // have started or extended a streak, which only the server knows.
            await loadFriendsData(true);
        } catch (error) {
            // No retry queue (D10) - put the UI back and let the caller report it.
            if (previous) setFriendsData(previous);
            console.log('Error updating play:', error);
            throw error;
        }
    };

    // ===== GAME ACTIVITY =====

    // Games with a play row for today. Derived, not tracked: the empty record
    // written on a game-link click is what puts a game in here.
    const todayPlays = useMemo(() => friendsData?.user?.today_plays || {}, [friendsData]);
    const gamesPlayedToday = useMemo(() => Object.keys(todayPlays).map(Number), [todayPlays]);

    const streak = friendsData?.user?.streak ?? 0;

    // "Has ever logged a score", as opposed to today's scores - derived
    // server-side from the same scan that produces the streak, because the
    // dashboard otherwise only knows about today. Home's ladder uses it to
    // tell a playlist-with-no-scores apart from a real habit (D41, rung 3).
    const hasEverScored = friendsData?.user?.hasEverScored ?? false;

    // Whether this account has ever created a share. Derived from the `shares`
    // collection rather than a stored flag - the last rung of Home's ladder
    // ends here, and D41's reason for not tracking it (a whole field for one
    // banner) stopped applying once shares had a collection of their own.
    const hasShared = friendsData?.user?.hasShared ?? false;

    // How many times this account has clicked through to play something. Home
    // uses it to decide whether returning from a game should offer the score
    // form automatically - see App.js.
    const gameClickCount = friendsData?.user?.gameClickCount ?? 0;

    const hasGameBeenPlayedToday = useCallback(
        (gameId) => Object.prototype.hasOwnProperty.call(todayPlays, String(gameId)),
        [todayPlays]
    );

    const handleGameLinkClick = useCallback(async (game) => {
        const success = await gameActivityService.handleGameLinkClick(
            game,
            createEmptyScoreRecord,
            favorites.includes(game?.id)
        );

        // Reflect the new play row immediately, without waiting for a reload
        // that this navigation is about to interrupt anyway.
        setFriendsData(prev => {
            if (!prev || !prev.user) return prev;
            if (Object.prototype.hasOwnProperty.call(prev.user.today_plays, String(game?.id))) return prev;
            return {
                ...prev,
                user: {
                    ...prev.user,
                    today_plays: {
                        ...prev.user.today_plays,
                        [String(game?.id)]: { score: null, message: '' }
                    }
                }
            };
        });

        return success;
    }, [favorites]);

    const checkForRecentGameVisit = useCallback(() => {
        return gameActivityService.checkForRecentGameVisit();
    }, []);

    const clearRecentGameVisit = useCallback(() => {
        return gameActivityService.clearRecentGameVisit();
    }, []);

    const hasScoreForToday = (gameId) => {
        const play = todayPlays[String(gameId)];
        return !!play && play.score !== null && play.score !== '';
    };

    const getGamePlayForToday = (gameId) => todayPlays[String(gameId)] || null;

    return (
        <PlayContext.Provider value={{
            friendsData,
            friendsLoading,
            friendsError,
            loadFriendsData,
            getFriendsDataFetchedAt,

            addFriend: addFriendHandler,
            removeFriend: removeFriendHandler,

            gamesPlayedToday,
            streak,
            hasEverScored,
            hasShared,
            gameClickCount,
            hasGameBeenPlayedToday,
            handleGameLinkClick,
            checkForRecentGameVisit,
            clearRecentGameVisit,

            updatePlay: updatePlayHandler,
            getGamePlayForToday,
            hasScoreForToday
        }}>
            {children}
        </PlayContext.Provider>
    );
};

export const usePlay = () => {
    const context = useContext(PlayContext);
    if (!context) {
        throw new Error('usePlay must be used within a PlayProvider');
    }
    return context;
};
